import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GroupsIcon from '@mui/icons-material/Groups';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import GavelIcon from '@mui/icons-material/Gavel';
import BrushIcon from '@mui/icons-material/Brush';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import PageContainer from "../components/page-container/PageContainer";
import darkLogo from '../logo_dark.svg';
import lightLogo from '../logo_light.svg';

const HomePage = () => {
  const theme = useTheme();
  const logo = theme.palette.mode === "dark" ? darkLogo : lightLogo;
  const isDarkMode = theme.palette.mode === "dark";

  const valueProps = [
    {
      icon: VerifiedUserIcon,
      title: 'Protect Your IP',
      description: 'Every document is watermarked for preview. Originals stay secure until payment.',
    },
    {
      icon: GroupsIcon,
      title: 'Control Access',
      description: 'Invite specific clients to view your work. No unauthorized sharing.',
    },
    {
      icon: AccountBalanceWalletIcon,
      title: 'Get Paid First',
      description: 'Downloads unlock only after payment confirmation. Fair compensation guaranteed.',
    },
  ];

  const steps = [
    { number: '1', title: 'Upload', description: 'Add your documents securely' },
    { number: '2', title: 'Invite', description: 'Share with specific clients' },
    { number: '3', title: 'Get Paid', description: 'Downloads unlock after payment' },
  ];

  const industries = [
    { icon: ArchitectureIcon, name: 'Construction' },
    { icon: GavelIcon, name: 'Legal' },
    { icon: BrushIcon, name: 'Design' },
    { icon: AccountBalanceIcon, name: 'Finance' },
  ];

  return (
    <PageContainer mt={0}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: 'white',
          py: { xs: 10, md: 14 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Box
            component="img"
            src={logo}
            alt="DocuVault Logo"
            sx={{
              width: { xs: 200, md: 320 },
              maxWidth: '90%',
              mb: 4
            }}
          />
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 600,
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            Secure Document Sharing for Professionals
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              opacity: 0.9,
              maxWidth: 600,
              mx: 'auto',
              mb: 4,
            }}
          >
            Protect your work with watermarked previews. Get paid before clients download.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            component={RouterLink}
            to="/signup"
            sx={{
              boxShadow: '0 4px 14px 0 rgba(255, 193, 7, 0.39)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(255, 193, 7, 0.5)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Start Free Trial
          </Button>
        </Container>
      </Box>

      {/* Value Proposition Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            align="center"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            Why DocuVault?
          </Typography>
          <Typography
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}
          >
            Built for professionals who need to protect their intellectual property and ensure fair payment.
          </Typography>
          <Grid container spacing={4}>
            {valueProps.map((prop) => {
              const IconComponent = prop.icon;
              return (
                <Grid size={{ xs: 12, md: 4 }} key={prop.title}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        bgcolor: isDarkMode ? 'primary.dark' : 'primary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <IconComponent
                        sx={{
                          fontSize: 36,
                          color: isDarkMode ? 'primary.light' : 'primary.main',
                        }}
                      />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {prop.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {prop.description}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          bgcolor: isDarkMode ? 'grey.900' : 'grey.50',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h4"
            align="center"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            How It Works
          </Typography>
          <Typography
            align="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            Simple, secure, straightforward.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: { xs: 4, md: 8 },
              flexWrap: 'wrap',
            }}
          >
            {steps.map((step, index) => (
              <Box
                key={step.number}
                sx={{
                  textAlign: 'center',
                  position: 'relative',
                  flex: '0 0 auto',
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '100%', sm: 160 },
                }}
              >
                {/* Connector line - only between steps on larger screens */}
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      position: 'absolute',
                      top: 28,
                      left: '100%',
                      width: { sm: 32, md: 64 },
                      height: 2,
                      bgcolor: 'primary.main',
                      opacity: 0.3,
                      ml: { sm: 2, md: 4 },
                    }}
                  />
                )}
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: '1.5rem',
                    fontWeight: 700,
                  }}
                >
                  {step.number}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Industries Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="md">
          <Typography
            variant="h4"
            align="center"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            Built for Your Industry
          </Typography>
          <Typography
            align="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            DocuVault serves professionals across diverse sectors.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: { xs: 4, md: 6 },
              flexWrap: 'wrap',
              mb: 4,
            }}
          >
            {industries.map((industry) => {
              const IconComponent = industry.icon;
              return (
                <Box key={industry.name} sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'primary.dark' : 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 1,
                    }}
                  >
                    <IconComponent
                      sx={{
                        fontSize: 32,
                        color: isDarkMode ? 'primary.light' : 'primary.main',
                      }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight={500}>
                    {industry.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="text"
              component={RouterLink}
              to="/pricing"
              endIcon={<ArrowForwardIcon />}
              sx={{ fontWeight: 500 }}
            >
              View all supported industries
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 10 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="h4" fontWeight={600} mb={2}>
            Ready to protect your professional work?
          </Typography>
          <Typography sx={{ opacity: 0.9, mb: 4, maxWidth: 500, mx: 'auto' }}>
            Join professionals who trust DocuVault to secure their documents and ensure fair payment.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              component={RouterLink}
              to="/signup"
              sx={{
                boxShadow: '0 4px 14px 0 rgba(255, 193, 7, 0.39)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(255, 193, 7, 0.5)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Start Free Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to="/pricing"
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              View Pricing
            </Button>
          </Box>
        </Container>
      </Box>
    </PageContainer>
  );
}

export default HomePage;
